import React, { useEffect, useState } from "react";
import Moment from 'moment';
import { useStore } from "../../../inbox-store";
import { useFeature } from "../../../../common/FeatureGate";
import { getUserLanguage, langName } from "../../../../../utils/languages";
import {
  List,
  Avatar,
  Space,
  Typography,
  Tooltip,
  Row,
  Col,
  Flex,
  Button,
  Popconfirm,
  Image,
} from "antd";

import {
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  ExclamationCircleFilled,
  LoadingOutlined,
  PaperClipOutlined,
  TranslationOutlined,
  LikeOutlined,
  MessageOutlined,
  PhoneOutlined,
  RobotOutlined,
  RollbackOutlined,
  SoundOutlined,
  StarOutlined,
  SwapLeftOutlined,
  SwapRightOutlined,
} from "@ant-design/icons";
import {
  getActiveTime,
  parseNewLine,
} from "../../helpers/ui";
import ViewerAvatarGroup from "../../../../common/ViewerAvatarGroup";
import PendingMessageBubble from "../../MessageList/PendingMessageBubble/PendingMessageBubble";

const { Text, Title } = Typography;

export const smallAvatarText: React.CSSProperties = {
  color: 'white',
  fontSize: "11px",
  alignItems: "center",
  justifyContent: "center",
  display: "grid",
};

// Viewer avatar logic moved to ViewerAvatarGroup

// Mirrors RED_MSG_STATUSES / provider rules from inbox/threads.js so the
// bubble-level failure indicator matches what the info dialog shows.
const FAILED_MSG_STATUSES = [null, 'expired', 'failed', 'rejected', 'unknown'];
const ALWAYS_DELIVERED_PROVIDERS = ['webchat', 'messenger', 'twilio'];

const isFailedOutbound = (item: any) => {
  if (item.direction !== 'outbound') return false;
  const provider = item.conversation?.botmap?.type;
  if (ALWAYS_DELIVERED_PROVIDERS.includes(provider)) return false;
  // No provider_id = old/bad data, not a real failure
  if (!item.provider_id) return false;
  if (provider === 'nexmo') {
    return FAILED_MSG_STATUSES.includes(item.message_status);
  }
  if (provider === 'messagemedia') {
    return item.message_status !== 'delivered';
  }
  return false;
};

// Inbound MMS media — backend signs S3 URLs into item.attachments
// as [{kind: 'image'|'video'|'audio'|'file'|'vcard', contentType, url}].
const ThreadAttachments = ({ attachments }: any) => {
  if (!Array.isArray(attachments) || attachments.length === 0) return null;
  return (
    <>
      {attachments.map((att: any, i: number) => {
        if (att.kind === "image") {
          return <Image key={i} src={att.url} style={{ maxWidth: 240, borderRadius: 8 }} />;
        }
        if (att.kind === "video") {
          return <video key={i} src={att.url} controls style={{ maxWidth: 240, borderRadius: 8 }} />;
        }
        if (att.kind === "audio") {
          return <audio key={i} src={att.url} controls style={{ maxWidth: 240 }} />;
        }
        return (
          <Button
            key={i}
            icon={<PaperClipOutlined />}
            size="small"
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Attachment{att.contentType ? ` (${att.contentType})` : ""}
          </Button>
        );
      })}
    </>
  );
};

// How long a fresh inbound bubble shows "Translating…" while waiting for the
// thread:translated socket event before offering a manual Translate link.
const TRANSLATING_GRACE_MS = 15000;

const ThreadItem = ({ item, openMsgInfoDialog, isFirst, reactionsByTarget, hasThread, contactLanguage: contactLanguageProp, translateThread }: any) => {
  const deleteScheduledMessageThread = useStore((state)=>state.deleteScheduledMessageThread)
  // ADR-035: ThreadList is hosted by the inbox AND by /contacts/:id (own store), so
  // take the contact language / translate action from props when the host supplies
  // them and only fall back to the inbox-v2 store (empty on the contact page).
  const storeTranslateThreadOnDemand = useStore((state) => state.translateThreadOnDemand);
  const storeContactLanguage = useStore((state) => state.activeContactModel?.language);
  const translateThreadOnDemand = translateThread || storeTranslateThreadOnDemand;
  const contactLanguage = contactLanguageProp !== undefined ? contactLanguageProp : storeContactLanguage;
  const canTranslate = useFeature('ai-translation');
  const isInbound = item.direction === "inbound";
  const isScheduled = item.direction === "scheduled";
  const isFailed = isFailedOutbound(item);

  // ── AI translation (ADR-035) ──
  // item.message is always the wire text; item.translation.alt[<lang>] is its
  // rendering in <lang>. Show the viewer's language as the primary text and keep
  // the wire text one click away (both visible for comparison, never swapped).
  const viewerLang = getUserLanguage();
  const translation = item.translation;
  const altText: string | undefined = translation?.alt?.[viewerLang];
  const showsTranslation = !!altText && !isScheduled;
  const [showOriginal, setShowOriginal] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateFailed, setTranslateFailed] = useState(false);
  // Fresh inbound from a contact in another language: the translation is on its way.
  const [withinGrace, setWithinGrace] = useState(() => {
    const age = Date.now() - new Date(item.created_at).getTime();
    return Number.isFinite(age) && age >= 0 && age < TRANSLATING_GRACE_MS;
  });
  useEffect(() => {
    if (!withinGrace) return undefined;
    const remaining = TRANSLATING_GRACE_MS - (Date.now() - new Date(item.created_at).getTime());
    const timer = setTimeout(() => setWithinGrace(false), Math.max(remaining, 0));
    return () => clearTimeout(timer);
  }, [withinGrace, item.created_at]);
  const wireLang: string | undefined = translation?.lang;
  const languageDiffers = isInbound
    ? !!contactLanguage && contactLanguage !== viewerLang
    : !!wireLang && wireLang !== 'und' && wireLang !== viewerLang;
  const offerTranslate = canTranslate && !showsTranslation && !isScheduled && !!item.message && languageDiffers;
  const onTranslateClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (translating) return;
    setTranslating(true);
    setTranslateFailed(false);
    try {
      await translateThreadOnDemand(item.id, viewerLang);
    } catch (err) {
      console.error('translate thread failed', err);
      setTranslateFailed(true);
    } finally {
      setTranslating(false);
    }
  };
  const captionColor = !isInbound ? 'rgba(255,255,255,0.85)' : '#8c8c8c';

  // Tapback reaction rows collapse into the emoji badge on their target bubble
  // when that bubble is on screen; unresolved ones render as normal text.
  if (item.reaction?.target_thread_id && hasThread?.(item.reaction.target_thread_id)) {
    return null;
  }
  const reactionEmojis: string[] = reactionsByTarget?.[item.id] || [];

  // ADR-027: kind-aware pending bubble rendering. Existing rows with kind=NULL
  // fall through to the legacy dashed bubble below.
  if (isScheduled && item.kind) {
    return (
      <Flex
        vertical
        style={{ marginBottom: '15px', padding: '0px 10px 0px 0px' }}
      >
        <PendingMessageBubble item={item} />
      </Flex>
    );
  }

  return (
    <Flex vertical 
    style={{
      marginBottom: isScheduled ? "15px" : "5px",
      padding: isInbound ? "0px 0px 0px 10px" : "0px 10px 0px 0px",
    }}
    >
      {isInbound && !isFirst(item) ? (
        <Text
          type="secondary"
          style={{
            marginTop: "10px",
            fontSize: "x-small",
            fontWeight: "normal",
            fontStyle: "italic",
            color: "#888",
            textAlign: "start",
          }}
        >
          <SwapRightOutlined /> Replying to {item.channelDetails?.name || (typeof item.channel_name === 'object' ? item.channel_name?.name : item.channel_name)}
        </Text>
      ) : (
        ""
      )}

   
      
        <List.Item
          className={isInbound ? "v2-in-thread" : "v2-out-thread"}
          key={item.id}
          actions={[
            <Space style={{marginTop:'4px', alignItems: 'center'}} >
              {isFailed && (
                <Tooltip title="Message could not be sent. Click for details.">
                  <ExclamationCircleFilled
                    style={{ color: "#f5222d", fontSize: 14, cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (typeof openMsgInfoDialog === "function") {
                        openMsgInfoDialog(item);
                      }
                    }}
                  />
                </Tooltip>
              )}
              <ClockCircleOutlined style={{ color: "#c5c5c5" }} />
              <Text
                type="secondary"
                style={{ fontSize: "x-small", fontWeight: 500, color: "#545147", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Thread timestamp clicked, opening dialog for item:', item);
                  if (typeof openMsgInfoDialog === 'function') {
                    openMsgInfoDialog(item);
                  } else {
                    console.error('openMsgInfoDialog is not a function:', openMsgInfoDialog);
                  }
                }}
              >
                {isScheduled ? `Scheduled for ` : ""}
                {isScheduled ? Moment(item.created_at).calendar() :getActiveTime(item.created_at)}{" "}
              </Text>
              
              {!isInbound ? (() => {
                // Prefer channelDetails (rich). Fall back to legacy object-shaped
                // channel_name. Last resort: string channel_name → initials only.
                const channelInfo = item.channelDetails
                  || (typeof item.channel_name === 'object' ? item.channel_name : null);
                if (channelInfo) {
                  return <ViewerAvatarGroup viewers={[{
                    name: channelInfo.name || 'User',
                    avatar_url: channelInfo.avatar_url,
                    type: channelInfo.type || 'user',
                    id: channelInfo.id
                  }]} />
                }
                // Fallback for string-only channel_name (initials via ViewerAvatarGroup)
                return <ViewerAvatarGroup viewers={[{
                  name: item.channel_name || 'User',
                  type: 'user',
                  id: '',
                  avatar_url: ''
                }]} />
              })() : ""}
              
            </Space>,
          ]}
        >
         <Space align="center" style={{alignSelf: isInbound? "flex-start": "flex-end"}}>
         {isScheduled ? (
               <Popconfirm
               title="Confirmation"
               description="Are you sure you want to remove this scheduled message?"
               onConfirm={async()=>{
                await deleteScheduledMessageThread(item.id)
               }}
               okText="Yes"
               cancelText="No"
               placement="left"
             >
               <Button type="text" size="small">
                 <Text
                   type="secondary"
                   style={{
                     fontSize: "x-small",
                     fontWeight: 500,
                     color: "#545147",
                   }}
                 >
                   Cancel
                 </Text>
               </Button>
              </Popconfirm>
           ) : (
             ""
           )}
          <List.Item.Meta
            className="chat-bubble"
            style={{
              textAlign: "start",
              position: "relative",
              // alignSelf: !isInbound ? "self-end" : "self-start",
              background: !isInbound ? ( isScheduled? "white":"#39b54a"):"#f0f0f0",
              border: isScheduled? "1px dashed gray" : "none"
            }}
            description={
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  {reactionEmojis.length > 0 ? (
                    <span
                      style={{
                        position: "absolute",
                        bottom: -10,
                        ...(isInbound ? { right: -6 } : { left: -6 }),
                        background: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: 12,
                        padding: "0 6px",
                        fontSize: 13,
                        lineHeight: "20px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }}
                    >
                      {reactionEmojis.join(" ")}
                    </span>
                  ) : null}
                  <ThreadAttachments attachments={item.attachments} />
                  {item.message ? (
                    <Text
                      style={{ width: "100%",
                        color: !isInbound ? ( isScheduled? "gray" : "#fff") : "#000",
                        fontStyle: isScheduled ? "italic": "normal",
                        fontSize: isScheduled ? "smaller":"inherit"
                      }}
                      ellipsis={false}
                    >
                      {parseNewLine(showsTranslation ? altText : item.message)}
                    </Text>
                  ) : null}
                  {showsTranslation && (
                    <div
                      className="gh-translation-caption"
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: captionColor, lineHeight: '16px' }}
                    >
                      <TranslationOutlined />
                      <span>
                        {isInbound
                          ? `Translated from ${langName(translation.lang) || 'another language'}`
                          : `Sent in ${langName(translation.lang) || 'another language'}`}
                      </span>
                      <span>·</span>
                      <a
                        role="button"
                        style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); setShowOriginal((v) => !v); }}
                      >
                        {showOriginal ? 'Hide' : (isInbound ? 'Show original' : 'Show sent text')}
                      </a>
                    </div>
                  )}
                  {showsTranslation && showOriginal && (
                    <Text
                      italic
                      style={{
                        width: '100%',
                        fontSize: 12,
                        color: !isInbound ? 'rgba(255,255,255,0.9)' : '#595959',
                        borderLeft: `2px solid ${!isInbound ? 'rgba(255,255,255,0.5)' : '#d9d9d9'}`,
                        paddingLeft: 8,
                      }}
                    >
                      {parseNewLine(item.message)}
                    </Text>
                  )}
                  {offerTranslate && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: captionColor, lineHeight: '16px' }}>
                      {isInbound && withinGrace && !translateFailed ? (
                        <>
                          <LoadingOutlined />
                          <span>Translating…</span>
                        </>
                      ) : (
                        <a
                          role="button"
                          style={{ color: 'inherit', textDecoration: 'underline', cursor: translating ? 'progress' : 'pointer' }}
                          onClick={onTranslateClick}
                        >
                          {translating ? <LoadingOutlined /> : <TranslationOutlined />}{' '}
                          {translateFailed ? 'Translation failed — retry' : `Translate to ${langName(viewerLang)}`}
                        </a>
                      )}
                    </div>
                  )}
                </Space>
            }
          />
         </Space>

         </List.Item>
          
      
    </Flex>
  );
};

export default ThreadItem;
